import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { apiErrorMsg } from '@/lib/formatters';

const DEMO_ACCOUNTS = [
  { label: 'Director', email: 'virworkings@gmail.com', password: 'VirAdmin@2026', role: 'Full System Access' },
  { label: 'Sales Manager', email: 'manager@virinfra.com', password: 'Manager@2026', role: 'Team & Lead Management' },
  { label: 'Sales Executive', email: 'rajesh.exec@virinfra.com', password: 'Executive@2026', role: 'Lead Operations' },
  { label: 'Finance', email: 'finance@virinfra.com', password: 'Finance@2026', role: 'Payments & Verification' },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(apiErrorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (account) => {
    setEmail(account.email);
    setPassword(account.password);
    setError('');
    setLoading(true);
    try {
      await login(account.email, account.password);
      navigate('/dashboard');
    } catch (err) {
      setError(apiErrorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-bg flex min-h-screen">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Building2 className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-white font-extrabold text-2xl tracking-tight">SalesOS</h1>
              <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold">Real Estate Sales & CRM</p>
            </div>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight mb-6">
            Every Lead.<br />
            Every Deal.<br />
            <span className="text-emerald-400">One System.</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-md leading-relaxed">
            Centralize leads, projects, bookings, and payments. Track every interaction from first call to possession.
          </p>
        </div>
        <div className="relative z-10">
          <p className="text-slate-500 text-sm">SalesOS — Real Estate Sales & CRM Platform</p>
        </div>
        {/* Background decoration */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white lg:rounded-l-[2rem]">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Building2 className="text-white" size={22} />
            </div>
            <div>
              <h1 className="text-slate-900 font-bold text-xl">SalesOS</h1>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Welcome back</h3>
            <p className="text-slate-500">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="email" type="email" placeholder="you@company.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="login-email-input" required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password" type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-slate-50 border-slate-200 focus:bg-white"
                  data-testid="login-password-input" required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Demo accounts */}
          <div className="mt-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Demo Accounts</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map(acc => (
                <button
                  key={acc.email}
                  onClick={() => quickLogin(acc)}
                  disabled={loading}
                  data-testid={`demo-login-${acc.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-left p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group"
                >
                  <p className="text-sm font-semibold text-slate-700 group-hover:text-emerald-700">{acc.label}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{acc.role}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
